import { AuthRequirements } from '../domain/object/authRequirements';

export const AUTH_REQUIREMENT_UNSATISFIED_CODE = 'auth.requirement.unsatisfied' as const;

export const SESSION_MFA_CUSTOM_CLAIM_KEYS = {
  signInSecondFactor: 'rt_mfa_sign_in_second_factor',
  secondFactorIdentifier: 'rt_mfa_second_factor_identifier',
} as const;

export const SESSION_SIGN_IN_PROVIDER_CUSTOM_CLAIM_KEY = 'rt_sign_in_provider' as const;

export type SessionMfaCustomClaims = {
  [SESSION_MFA_CUSTOM_CLAIM_KEYS.signInSecondFactor]?: string;
  [SESSION_MFA_CUSTOM_CLAIM_KEYS.secondFactorIdentifier]?: string;
};

export type SessionAuthCustomClaims = SessionMfaCustomClaims & {
  [SESSION_SIGN_IN_PROVIDER_CUSTOM_CLAIM_KEY]?: string;
};

type FirebaseMfaTokenLike = Record<string, unknown> & {
  firebase?: {
    sign_in_second_factor?: unknown;
    second_factor_identifier?: unknown;
    sign_in_provider?: unknown;
  };
};

const stringClaim = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined);

export const toSessionMfaCustomClaims = (claims: AuthRequirements.MfaClaims): SessionMfaCustomClaims => ({
  ...(claims.signInSecondFactor ? { [SESSION_MFA_CUSTOM_CLAIM_KEYS.signInSecondFactor]: claims.signInSecondFactor } : {}),
  ...(claims.secondFactorIdentifier ? { [SESSION_MFA_CUSTOM_CLAIM_KEYS.secondFactorIdentifier]: claims.secondFactorIdentifier } : {}),
});

export const toSessionAuthCustomClaims = (params: {
  mfa: AuthRequirements.MfaClaims;
  signInProvider?: string | undefined;
}): SessionAuthCustomClaims => ({
  ...toSessionMfaCustomClaims(params.mfa),
  ...(params.signInProvider ? { [SESSION_SIGN_IN_PROVIDER_CUSTOM_CLAIM_KEY]: params.signInProvider } : {}),
});

export const extractMfaClaimsFromToken = (token: FirebaseMfaTokenLike): AuthRequirements.MfaClaims => ({
  signInSecondFactor:
    stringClaim(token.firebase?.sign_in_second_factor) ?? stringClaim(token[SESSION_MFA_CUSTOM_CLAIM_KEYS.signInSecondFactor]),
  secondFactorIdentifier:
    stringClaim(token.firebase?.second_factor_identifier) ?? stringClaim(token[SESSION_MFA_CUSTOM_CLAIM_KEYS.secondFactorIdentifier]),
});

export const extractSignInProviderFromToken = (token: FirebaseMfaTokenLike): string | undefined => {
  const nativeProvider = stringClaim(token.firebase?.sign_in_provider);
  if (nativeProvider && nativeProvider !== 'custom') return nativeProvider;
  return stringClaim(token[SESSION_SIGN_IN_PROVIDER_CUSTOM_CLAIM_KEY]) ?? nativeProvider;
};

export type AuthRequirementKind = 'mfa' | 'sso';

export type AuthRequirementErrorResponse = {
  code: typeof AUTH_REQUIREMENT_UNSATISFIED_CODE;
  requirement: AuthRequirementKind;
  organizationId?: string;
  projectId?: string;
};

export type AuthRequirementErrorLike = AuthRequirementError | AuthRequirementErrorResponse;

export class AuthRequirementError extends Error {
  readonly code = AUTH_REQUIREMENT_UNSATISFIED_CODE;
  readonly requirement: AuthRequirementKind;
  readonly organizationId: string | undefined;
  readonly projectId: string | undefined;

  constructor(params: { requirement: AuthRequirementKind; organizationId?: string; projectId?: string }) {
    super(`${params.requirement.toUpperCase()} is required.`);
    this.name = 'AuthRequirementError';
    this.requirement = params.requirement;
    this.organizationId = params.organizationId;
    this.projectId = params.projectId;
  }
}

type RecordLike = Record<string, unknown>;

const toRecord = (value: unknown): RecordLike | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as RecordLike;
};

const isAuthRequirementKind = (value: unknown): value is AuthRequirementKind => value === 'mfa' || value === 'sso';

export const isAuthRequirementErrorResponse = (value: unknown): value is AuthRequirementErrorResponse => {
  const record = toRecord(value);
  return record?.code === AUTH_REQUIREMENT_UNSATISFIED_CODE && isAuthRequirementKind(record.requirement);
};

export const isAuthRequirementError = (error: unknown): error is AuthRequirementErrorLike => {
  if (error instanceof AuthRequirementError) return true;
  if (isAuthRequirementErrorResponse(error)) return true;
  const record = toRecord(error);
  return (
    record?.name === 'AuthRequirementError' &&
    record.code === AUTH_REQUIREMENT_UNSATISFIED_CODE &&
    isAuthRequirementKind(record.requirement)
  );
};

export const toAuthRequirementErrorResponse = (error: AuthRequirementErrorLike): AuthRequirementErrorResponse => ({
  code: error.code,
  requirement: error.requirement,
  ...(error.organizationId ? { organizationId: error.organizationId } : {}),
  ...(error.projectId ? { projectId: error.projectId } : {}),
});

type ProjectWithAuthRequirements = {
  id: string;
  authRequirements: AuthRequirements;
};

type OrganizationWithAuthRequirements = {
  id: string;
  authRequirements: AuthRequirements;
  projects: ProjectWithAuthRequirements[];
};

const unsatisfiedRequirement = (requirements: AuthRequirements): AuthRequirementKind | undefined => {
  if (AuthRequirements.IsMfaRequiredAndUnsatisfied(requirements)) return 'mfa';
  if (AuthRequirements.IsSsoRequiredAndUnsatisfied(requirements)) return 'sso';
  return undefined;
};

export const assertAuthRequirementsSatisfied = (params: {
  requirements: AuthRequirements;
  organizationId?: string;
  projectId?: string;
}): void => {
  const requirement = unsatisfiedRequirement(params.requirements);
  if (!requirement) return;
  throw new AuthRequirementError({
    requirement,
    organizationId: params.organizationId ?? params.requirements.sso.provider?.organizationId,
    projectId: params.projectId,
  });
};

export const assertOrganizationAuthRequirementsSatisfied = (params: {
  organizations: OrganizationWithAuthRequirements[];
  organizationId: string;
}): void => {
  const organization = params.organizations.find((o) => o.id === params.organizationId);
  if (!organization) return;
  assertAuthRequirementsSatisfied({
    requirements: organization.authRequirements,
    organizationId: organization.id,
  });
};

export const assertProjectAuthRequirementsSatisfied = (params: {
  organizations: OrganizationWithAuthRequirements[];
  projectId: string;
}): void => {
  const organization = params.organizations.find((o) => o.projects.some((p) => p.id === params.projectId));
  const project = organization?.projects.find((p) => p.id === params.projectId);
  if (!project) return;
  assertAuthRequirementsSatisfied({
    requirements: project.authRequirements,
    organizationId: organization?.id,
    projectId: project.id,
  });
};
